import { useConfig } from "../contexts/ConfigContext";
import { contentPath, modules, isViewAllowed } from "../config/const";
import React from "react";
import {
  ApartmentOutlined,
  BarChartOutlined,
  BookOutlined,
  DashboardOutlined,
  FileTextOutlined,
  LineChartOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { ItemType } from "antd/es/menu/interface";
import { useUser } from "../contexts/UserContext";
import { SideBar as SharedSideBar } from "@ryo/ui";

const generalOptions = [
  { key: modules.Dashboard, label: "Dashboard Contable", icon: <DashboardOutlined /> },
  { key: modules.ChartOfAccounts, label: "Plan de Cuentas (PUC)", icon: <ApartmentOutlined /> },
  { key: modules.JournalEntries, label: "Comprobantes de Diario", icon: <FileTextOutlined /> },
];

const reportOptions = [
  { key: modules.TrialBalance, label: "Balance de Comprobación", icon: <BarChartOutlined /> },
  { key: modules.GeneralLedger, label: "Libro Mayor y Auxiliar", icon: <BookOutlined /> },
  { key: modules.IncomeStatement, label: "Estado de Resultados", icon: <LineChartOutlined /> },
];

const settingOptions = [
  { key: modules.AccountingSettings, label: "Cuentas del Sistema", icon: <SettingOutlined /> },
];

function getMenuItems(permissions: any) {
  const views = (permissions?.views || []).map((v: string) => v.toLowerCase());
  if (views.length === 0) return [];

  const availableGeneral = generalOptions.filter(el => isViewAllowed(el.key, views));
  const availableReports = reportOptions.filter(el => isViewAllowed(el.key, views));
  const availableSettings = settingOptions.filter(el => isViewAllowed(el.key, views));

  const menuItems: ItemType[] = [];

  if (availableGeneral.length) {
    menuItems.push({
      key: "accounting_ops",
      icon: <ApartmentOutlined />,
      label: "Contabilidad",
      children: availableGeneral as any,
    });
  }

  if (availableReports.length) {
    menuItems.push({
      key: "accounting_reports",
      icon: <BarChartOutlined />,
      label: "Informes Financieros",
      children: availableReports as any,
    });
  }

  if (availableSettings.length) {
    menuItems.push({
      key: "accounting_config",
      icon: <SettingOutlined />,
      label: "Configuración",
      children: availableSettings as any,
    });
  }

  return menuItems;
}

const getParentKey = (key: string, menuItems: ItemType[]) => {
  const parent = menuItems.find(item =>
    item && "children" in item && Array.isArray((item as any).children) &&
    (item as any).children.some((child: any) => child.key === key)
  );
  return parent?.key?.toString() ?? null;
};

function SideBar() {
  const history = useHistory();
  const { config, updateConfig } = useConfig();
  const { user } = useUser();

  const menuItems = React.useMemo(() => getMenuItems(user.permissions), [user.permissions]);
  const [openKey, setOpenKey] = React.useState<string | null>(getParentKey(config.module, menuItems));

  React.useEffect(() => {
    setOpenKey(getParentKey(config.module, menuItems));
  }, [config.module, menuItems]);

  const handleOpenChange = (keys: string[]) => {
    setOpenKey(keys.at(-1) ?? null);
  };

  const toggleCollapsed = () => {
    updateConfig({ sideCollapsed: !config.sideCollapsed });
  };

  const handleSelected = (key: string) => {
    const moduleSelectedKey = Object.keys(modules).find(k => modules[k as keyof typeof modules] === key);
    const module = modules[moduleSelectedKey as keyof typeof modules];
    if (module && contentPath[module]) {
      updateConfig({ module });
      history.push(contentPath[module]);
    }
  };

  return (
    <SharedSideBar
      activeKey={config.module}
      collapsed={config.sideCollapsed}
      isMobile={config.isMobile}
      menuItems={menuItems}
      openKey={openKey}
      onToggle={toggleCollapsed}
      onSelect={handleSelected}
      onOpenChange={handleOpenChange}
      title="Ryo Contabilidad"
      logoSrc="/assets/icons/accounting-sidebar.png"
    />
  );
}

export default SideBar;
